import type { DimensionConfig } from '@/spaces/campaigns/CampaignLibraryFilters';
import type { ActiveFilter } from '@/spaces/campaigns/campaignLibraryFilterUtils';
import { creativityBand } from '@/spaces/campaigns/strategyFormat';

import type { StrategyListItem } from '../../../../../bridge/types/strategy.ts';

import { strategyGroupOf } from './strategyDockData';

// The dock's filter dimensions. `FilterToolbar` and the ActiveFilter/DimensionConfig types are
// reused from the campaigns library; only the matcher below is ours, since that module ships
// types and a toolbar but no predicate.
export type StrategyFilter = ActiveFilter<'status' | 'tier', string>;

export const STRATEGY_FILTER_DIMENSIONS: DimensionConfig[] = [
	{
		key: 'status',
		label: 'Status',
		options: [
			{ label: 'Active', value: 'active' },
			{ label: 'Paused', value: 'paused' },
			{ label: 'Generating', value: 'generating' },
			{ label: 'New', value: 'new' },
		],
	},
	{
		key: 'tier',
		label: 'Tier',
		options: [
			{ label: 'Proven', value: 'mid' },
			{ label: 'Bold', value: 'bold' },
			{ label: 'Big swing', value: 'stunt' },
		],
	},
];

function matchesDimension(
	item: StrategyListItem,
	dimension: string,
	values: string[],
): boolean {
	if (dimension === 'status') {
		return values.includes(strategyGroupOf(item));
	}
	if (dimension === 'tier') {
		return values.includes(creativityBand(item.creativity_level));
	}
	return true;
}

// OR within a dimension, AND across dimensions — the semantics the campaigns and content
// libraries already use, so a filter behaves the same wherever the owner meets one.
export function applyStrategyFilters(
	items: StrategyListItem[],
	filters: StrategyFilter[],
): StrategyListItem[] {
	if (filters.length === 0) return items;
	const byDimension = new Map<string, string[]>();
	for (const f of filters) {
		byDimension.set(f.dimension, [
			...(byDimension.get(f.dimension) ?? []),
			f.value,
		]);
	}
	return items.filter((item) =>
		[...byDimension.entries()].every(([dimension, values]) =>
			matchesDimension(item, dimension, values),
		),
	);
}
